import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { WorkOrderStatusBadge, AssetStatusBadge, ConditionBadge } from '@/components/shared/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Separator } from '@/components/ui/separator';
import PhotoUpload from '@/components/shared/PhotoUpload';
import {
  ClipboardList, ScanLine, Plus, Printer, ArrowLeft, Package, ListOrdered, Grid3X3,
  CheckCircle2, Zap, AlertCircle, Eye,
} from 'lucide-react';
import { addMockIntakeLine, createMockAsset } from '@/data/mockData';
import { generateInternalBarcode, checkDuplicateIdentifier, addAssetToList } from '@/data/mockAssets';
import { useWorkOrder, useIntakeLines } from '@/hooks/useWorkOrders';
import { useCategories, useManufacturers } from '@/hooks/useSettings';
import BulkSerialDialog from '@/components/intake/BulkSerialDialog';
import ConvertLineDialog from '@/components/intake/ConvertLineDialog';
import LabelPreviewDialog from '@/components/intake/LabelPreviewDialog';
import { toast } from 'sonner';
import type { IntakeMode, AssetCondition, Asset, IntakeLine } from '@/types/models';


type IdentificationMethod = 'scan' | 'manual' | 'auto';

const CONDITION_OPTIONS: { value: AssetCondition; label: string; colorVar: string }[] = [
  { value: 'new', label: 'New', colorVar: '--condition-new' },
  { value: 'like_new', label: 'Like New', colorVar: '--condition-like-new' },
  { value: 'good', label: 'Good', colorVar: '--condition-good' },
  { value: 'fair', label: 'Fair', colorVar: '--condition-fair' },
  { value: 'poor', label: 'Poor', colorVar: '--condition-poor' },
  { value: 'damaged', label: 'Damaged', colorVar: '--condition-damaged' },
  { value: 'parts_only', label: 'Parts Only', colorVar: '--condition-parts' },
  { value: 'scrap', label: 'Scrap', colorVar: '--condition-scrap' },
];

interface SessionAsset extends Asset {
  _justCreated?: boolean;
}

export default function IntakePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: wo, isLoading: woLoading } = useWorkOrder(id);
  const { data: fetchedIntakeLines = [] } = useIntakeLines(id);
  const { data: categoriesData = [] } = useCategories();
  const { data: manufacturersData = [] } = useManufacturers();

  const [mode, setMode] = useState<IntakeMode>('detailed');
  const [recentAssets, setRecentAssets] = useState<SessionAsset[]>([]);
  const [idMethod, setIdMethod] = useState<IdentificationMethod>('scan');

  // Detailed mode state
  const [serialInput, setSerialInput] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [model, setModel] = useState('');
  const [condition, setCondition] = useState<AssetCondition>('good');
  const [weight, setWeight] = useState('');
  const [assetNotes, setAssetNotes] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [lastCreated, setLastCreated] = useState<SessionAsset | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [autoBarcode, setAutoBarcode] = useState<string | null>(null);
  const serialRef = useRef<HTMLInputElement>(null);

  // Bulk mode state
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState<number>(0);

  // Dialogs
  const [bulkSerialOpen, setBulkSerialOpen] = useState(false);
  const [convertLine, setConvertLine] = useState<IntakeLine | null>(null);
  const [labelAsset, setLabelAsset] = useState<Asset | null>(null);

  const [intakeLines, setIntakeLines] = useState<IntakeLine[]>([]);
  const [linesInitialized, setLinesInitialized] = useState(false);

  // Sync fetched intake lines into local state once
  useEffect(() => {
    if (fetchedIntakeLines.length > 0 && !linesInitialized) {
      setIntakeLines(fetchedIntakeLines);
      setLinesInitialized(true);
    }
  }, [fetchedIntakeLines, linesInitialized]);

  useEffect(() => {
    serialRef.current?.focus();
  }, [mode]);

  // Auto-generate barcode when method is 'auto'
  useEffect(() => {
    if (idMethod === 'auto') {
      setAutoBarcode(generateInternalBarcode());
      setSerialInput('');
      setDuplicateError(null);
    } else {
      setAutoBarcode(null);
    }
  }, [idMethod]);

  if (woLoading) {
    return <div className="flex h-64 items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  }

  if (!wo) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Work order not found.</p>
      </div>
    );
  }

  function handleCreateAsset() {
    if (!categoryId) return;

    const serial = idMethod === 'auto' ? undefined : (serialInput || undefined);
    const barcode = idMethod === 'auto' ? autoBarcode! : undefined;

    // Duplicate check
    if (serial || barcode) {
      const dup = checkDuplicateIdentifier(serial, barcode);
      if (dup) {
        setDuplicateError(`Duplicate found: ${dup.internal_barcode} (${dup.category_name})`);
        return;
      }
    }

    const asset = createMockAsset({
      work_order_id: wo.id,
      serial_number: serial,
      barcode: barcode,
      category_id: categoryId,
      category_name: categoriesData.find((c) => c.id === categoryId)?.name,
      manufacturer_id: manufacturerId || undefined,
      manufacturer_name: manufacturersData.find((m) => m.id === manufacturerId)?.name,
      model: model || undefined,
      condition,
      status: 'received',
      tracking_mode: 'serialized',
      quantity: 1,
      weight: weight ? parseFloat(weight) : undefined,
      site_id: wo.site_id,
      notes: assetNotes || undefined,
      scanned_by: wo.assigned_operator,
    });

    addAssetToList(asset);
    setRecentAssets((prev) => [{ ...asset, _justCreated: true }, ...prev]);
    setLastCreated(asset);
    setDuplicateError(null);
    // Reset form
    setSerialInput('');
    setCategoryId('');
    setManufacturerId('');
    setModel('');
    setCondition('good');
    setWeight('');
    setAssetNotes('');
    setPhotos([]);
    // Regenerate auto barcode for next item
    if (idMethod === 'auto') {
      setAutoBarcode(generateInternalBarcode());
    }
    serialRef.current?.focus();
  }

  function handleAddBulkLine() {
    if (!bulkCategoryId || bulkQuantity <= 0) return;
    const line = addMockIntakeLine({
      work_order_id: wo.id,
      category_id: bulkCategoryId,
      category_name: categoriesData.find((c) => c.id === bulkCategoryId)?.name,
      intake_mode: 'bulk',
      quantity_total: bulkQuantity,
      quantity_serialized: 0,
      quantity_remaining: bulkQuantity,
    });
    setIntakeLines((prev) => [...prev, line]);
    setBulkCategoryId('');
    setBulkQuantity(0);
  }

  function handleConvertDone(updated: IntakeLine, assets: Asset[]) {
    setIntakeLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setRecentAssets((prev) => [...assets.map((a) => ({ ...a, _justCreated: true })), ...prev]);
    if (assets.length > 0) {
      setLastCreated(assets[0]);
    }
  }

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/work-orders/${wo.id}`)} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> {wo.work_order_number}
        </Button>
      </div>

      {/* WO Header Card */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-mono font-semibold text-foreground">{wo.work_order_number}</p>
              <p className="text-xs text-muted-foreground">{wo.customer_name} · {wo.work_order_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setBulkSerialOpen(true)}>
              <Zap className="h-3.5 w-3.5" /> Bulk Generate
            </Button>
            <WorkOrderStatusBadge status={wo.status} />
          </div>
        </CardContent>
      </Card>

      {/* Mode Toggle */}
      <div className="mb-6">
        <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v as IntakeMode)} className="bg-muted rounded-lg p-1">
          <ToggleGroupItem value="detailed" className="gap-2 flex-1 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md px-4 py-2">
            <ListOrdered className="h-4 w-4" /> Detailed
          </ToggleGroupItem>
          <ToggleGroupItem value="bulk" className="gap-2 flex-1 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md px-4 py-2">
            <Grid3X3 className="h-4 w-4" /> Bulk
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Left Panel – Input */}
        <div className="lg:col-span-3 space-y-4">
          {mode === 'detailed' ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <ScanLine className="h-5 w-5 text-primary" />
                  <p className="text-sm font-semibold text-foreground">Scan / Enter Asset</p>
                </div>

                {/* Identification Method Toggle */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Identification Method</Label>
                  <ToggleGroup
                    type="single"
                    value={idMethod}
                    onValueChange={(v) => v && setIdMethod(v as IdentificationMethod)}
                    className="bg-muted rounded-md p-0.5 w-full"
                  >
                    <ToggleGroupItem value="scan" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-sm rounded px-3 py-1.5">
                      Scan Barcode
                    </ToggleGroupItem>
                    <ToggleGroupItem value="manual" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-sm rounded px-3 py-1.5">
                      Manual Entry
                    </ToggleGroupItem>
                    <ToggleGroupItem value="auto" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-sm rounded px-3 py-1.5">
                      Auto-Generate
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                {/* Serial / Barcode Input */}
                {idMethod !== 'auto' ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {idMethod === 'scan' ? 'Serial Number / Barcode' : 'Serial Number'}
                    </Label>
                    <Input
                      ref={serialRef}
                      value={serialInput}
                      onChange={(e) => {
                        setSerialInput(e.target.value);
                        setDuplicateError(null);
                      }}
                      placeholder={idMethod === 'scan' ? 'Scan or type serial number...' : 'Enter serial number...'}
                      className={`text-base h-12 font-mono ${duplicateError ? 'border-destructive' : ''}`}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateAsset()}
                    />
                    {duplicateError && (
                      <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {duplicateError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <Label className="text-xs text-muted-foreground">Generated Internal Barcode</Label>
                    <p className="text-lg font-mono font-bold text-primary mt-1">{autoBarcode}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">This barcode will be assigned to the next created asset</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Category *</Label>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {categoriesData.filter((c) => c.is_active).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Manufacturer</Label>
                    <Select value={manufacturerId} onValueChange={setManufacturerId}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {manufacturersData.filter((m) => m.is_active).map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Model</Label>
                    <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Latitude 5540" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Condition</Label>
                    <Select value={condition} onValueChange={(v) => setCondition(v as AssetCondition)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITION_OPTIONS.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: `hsl(var(${c.colorVar}))` }}
                              />
                              {c.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Optional"
                      min={0}
                      step={0.1}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <Textarea value={assetNotes} onChange={(e) => setAssetNotes(e.target.value)} rows={2} placeholder="Optional..." />
                </div>

                {/* Photo Capture */}
                <PhotoUpload photos={photos} onPhotosChange={setPhotos} label="Photos (equipment, asset tags, damage)" />

                <Button onClick={handleCreateAsset} disabled={!categoryId} className="w-full gap-2">
                  <Plus className="h-4 w-4" /> Create Asset
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Grid3X3 className="h-5 w-5 text-primary" />
                  <p className="text-sm font-semibold text-foreground">Add Bulk Intake Line</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categoriesData.filter((c) => c.is_active).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Quantity</Label>
                    <Input
                      type="number"
                      value={bulkQuantity || ''}
                      onChange={(e) => setBulkQuantity(parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                </div>

                <Button onClick={handleAddBulkLine} disabled={!bulkCategoryId || bulkQuantity <= 0} className="w-full gap-2">
                  <Plus className="h-4 w-4" /> Add Intake Line
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Last Created Asset */}
          {lastCreated && (
            <Card className="border-status-complete/30 bg-status-complete/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-status-complete" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">Asset Created</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {lastCreated.internal_barcode} · {lastCreated.category_name}
                      {lastCreated.serial_number && ` · S/N: ${lastCreated.serial_number}`}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setLabelAsset(lastCreated)}>
                      <Eye className="h-3.5 w-3.5" /> Preview
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => toast.success('Printing label...')}>
                      <Printer className="h-3.5 w-3.5" /> Print
                    </Button>
                  </div>
                </div>
                {!lastCreated.serial_number && !lastCreated.barcode && (
                  <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    No external identifier — attach the printed label to this item
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel – Activity */}
        <div className="lg:col-span-2 space-y-4">
          {/* Intake Lines */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-foreground">Intake Lines</p>
                <Badge variant="secondary" className="text-xs">{intakeLines.length}</Badge>
              </div>
              {intakeLines.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No intake lines yet.</p>
              ) : (
                <div className="space-y-3">
                  {intakeLines.map((line) => {
                    const pct = line.quantity_total > 0 ? Math.round((line.quantity_serialized / line.quantity_total) * 100) : 0;
                    return (
                      <div key={line.id} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground">{line.category_name}</p>
                            <Badge variant="outline" className="text-[10px] capitalize">{line.intake_mode}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{line.quantity_serialized}/{line.quantity_total}</p>
                        </div>
                        <Progress value={pct} className="h-1.5" />
                        <div className="mt-1 flex items-center justify-between">
                          <p className="text-[10px] text-muted-foreground">{line.quantity_remaining} remaining</p>
                          {line.quantity_remaining > 0 && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-[10px]"
                              onClick={() => setConvertLine(line)}
                            >
                              Convert to Serialized
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Scans */}
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-foreground mb-3">Recent Scans</p>
              {recentAssets.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No assets created this session.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recentAssets.map((asset) => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between rounded border border-border p-2 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setLabelAsset(asset)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-mono font-medium text-foreground">{asset.internal_barcode}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {asset.category_name}
                          {asset.serial_number && ` · ${asset.serial_number}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <ConditionBadge condition={asset.condition} />
                        <AssetStatusBadge status={asset.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <BulkSerialDialog open={bulkSerialOpen} onOpenChange={setBulkSerialOpen} />
      <ConvertLineDialog
        open={!!convertLine}
        onOpenChange={(open) => !open && setConvertLine(null)}
        line={convertLine}
        siteId={wo.site_id}
        workOrderId={wo.id}
        operator={wo.assigned_operator}
        onConverted={handleConvertDone}
      />
      <LabelPreviewDialog
        open={!!labelAsset}
        onOpenChange={(open) => !open && setLabelAsset(null)}
        asset={labelAsset}
        workOrderNumber={wo.work_order_number}
      />
    </>
  );
}
